import { z } from 'zod';
import {
  ACTIVITY_STATUSES,
  CHECK_IN_TYPES,
  GOAL_PRIORITIES,
  GOAL_STATUSES,
  LIMITS,
  MOODS,
  NOTE_TYPES,
  PAGINATION,
  PASSWORD_POLICY,
  REMINDER_TYPES,
  SOURCES,
} from '../constants';

const trimmedMinMax = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min)
    .max(max);

/** YYYY-MM-DD (local calendar date) */
export const dateStringSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected date format YYYY-MM-DD');

/** HH:mm 24h local time */
export const timeStringSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected time HH:mm');

export const isoDateTimeSchema = z.string().datetime({ offset: true });

export const idSchema = z.string().uuid();

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(PAGINATION.maxLimit).default(PAGINATION.defaultLimit),
});
export type PaginationInput = z.infer<typeof paginationSchema>;

export const goalStatusSchema = z.enum(GOAL_STATUSES);
export const goalPrioritySchema = z.enum(GOAL_PRIORITIES);
export const activityStatusSchema = z.enum(ACTIVITY_STATUSES);
export const sourceSchema = z.enum(SOURCES);
export const noteTypeSchema = z.enum(NOTE_TYPES);
export const checkInTypeSchema = z.enum(CHECK_IN_TYPES);
export const moodSchema = z.enum(MOODS);
export const reminderTypeSchema = z.enum(REMINDER_TYPES);

export const goalCreateSchema = z.object({
  title: trimmedMinMax(LIMITS.goalTitle.min, LIMITS.goalTitle.max),
  description: z.string().max(LIMITS.goalDescription.max).default(''),
  status: goalStatusSchema.default('active'),
  priority: goalPrioritySchema.default('medium'),
  targetDate: dateStringSchema.nullable().optional(),
  source: sourceSchema.default('dashboard'),
});
export type GoalCreateInput = z.infer<typeof goalCreateSchema>;

export const goalUpdateSchema = z
  .object({
    title: trimmedMinMax(LIMITS.goalTitle.min, LIMITS.goalTitle.max).optional(),
    description: z.string().max(LIMITS.goalDescription.max).optional(),
    status: goalStatusSchema.optional(),
    priority: goalPrioritySchema.optional(),
    targetDate: dateStringSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type GoalUpdateInput = z.infer<typeof goalUpdateSchema>;

export const goalListQuerySchema = paginationSchema.extend({
  status: goalStatusSchema.optional(),
  priority: goalPrioritySchema.optional(),
  sort: z.enum(['priority', 'target_date', 'created_at']).default('priority'),
});
export type GoalListQuery = z.infer<typeof goalListQuerySchema>;

export const activityCreateSchema = z.object({
  title: trimmedMinMax(LIMITS.activityTitle.min, LIMITS.activityTitle.max),
  description: z.string().max(LIMITS.activityDescription.max).default(''),
  goalId: idSchema.nullable().optional(),
  activityDate: dateStringSchema,
  startedAt: isoDateTimeSchema.nullable().optional(),
  endedAt: isoDateTimeSchema.nullable().optional(),
  durationMinutes: z
    .number()
    .int()
    .min(LIMITS.activityDurationMinutes.min)
    .max(LIMITS.activityDurationMinutes.max)
    .nullable()
    .optional(),
  status: activityStatusSchema.default('planned'),
  source: sourceSchema.default('dashboard'),
});
export type ActivityCreateInput = z.infer<typeof activityCreateSchema>;

export const activityUpdateSchema = z
  .object({
    title: trimmedMinMax(LIMITS.activityTitle.min, LIMITS.activityTitle.max).optional(),
    description: z.string().max(LIMITS.activityDescription.max).optional(),
    goalId: idSchema.nullable().optional(),
    activityDate: dateStringSchema.optional(),
    startedAt: isoDateTimeSchema.nullable().optional(),
    endedAt: isoDateTimeSchema.nullable().optional(),
    durationMinutes: z
      .number()
      .int()
      .min(LIMITS.activityDurationMinutes.min)
      .max(LIMITS.activityDurationMinutes.max)
      .nullable()
      .optional(),
    status: activityStatusSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type ActivityUpdateInput = z.infer<typeof activityUpdateSchema>;

export const activityListQuerySchema = paginationSchema.extend({
  status: activityStatusSchema.optional(),
  goalId: idSchema.optional(),
  from: dateStringSchema.optional(),
  to: dateStringSchema.optional(),
  sort: z.enum(['activity_date', 'created_at', 'duration']).default('activity_date'),
});
export type ActivityListQuery = z.infer<typeof activityListQuerySchema>;

export const tagCreateSchema = z.object({
  name: trimmedMinMax(LIMITS.tagName.min, LIMITS.tagName.max),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#64748b'),
});
export type TagCreateInput = z.infer<typeof tagCreateSchema>;

export const noteCreateSchema = z.object({
  content: trimmedMinMax(LIMITS.noteContent.min, LIMITS.noteContent.max),
  noteType: noteTypeSchema.default('quick_note'),
  goalId: idSchema.nullable().optional(),
  tagNames: z.array(trimmedMinMax(LIMITS.tagName.min, LIMITS.tagName.max)).max(10).default([]),
  source: sourceSchema.default('dashboard'),
});
export type NoteCreateInput = z.infer<typeof noteCreateSchema>;

export const noteUpdateSchema = z
  .object({
    content: trimmedMinMax(LIMITS.noteContent.min, LIMITS.noteContent.max).optional(),
    noteType: noteTypeSchema.optional(),
    goalId: idSchema.nullable().optional(),
    tagNames: z.array(trimmedMinMax(LIMITS.tagName.min, LIMITS.tagName.max)).max(10).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type NoteUpdateInput = z.infer<typeof noteUpdateSchema>;

export const noteListQuerySchema = paginationSchema.extend({
  noteType: noteTypeSchema.optional(),
  tag: z.string().max(LIMITS.tagName.max).optional(),
  goalId: idSchema.optional(),
  from: dateStringSchema.optional(),
  to: dateStringSchema.optional(),
  q: z.string().max(LIMITS.searchQuery.max).optional(),
});
export type NoteListQuery = z.infer<typeof noteListQuerySchema>;

export const checkInCreateSchema = z.object({
  checkInType: checkInTypeSchema.default('custom'),
  mood: moodSchema.nullable().optional(),
  energy: z.number().int().min(LIMITS.energy.min).max(LIMITS.energy.max).nullable().optional(),
  summary: z.string().max(LIMITS.summary.max).nullable().optional(),
  checkInDate: dateStringSchema,
  source: sourceSchema.default('dashboard'),
});
export type CheckInCreateInput = z.infer<typeof checkInCreateSchema>;

export const checkInListQuerySchema = paginationSchema.extend({
  from: dateStringSchema.optional(),
  to: dateStringSchema.optional(),
  checkInType: checkInTypeSchema.optional(),
});
export type CheckInListQuery = z.infer<typeof checkInListQuerySchema>;

export const searchQuerySchema = z.object({
  q: z.string().trim().min(LIMITS.searchQuery.min).max(LIMITS.searchQuery.max),
  type: z.enum(['goals', 'notes', 'activities', 'all']).default('all'),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export const reviewWeekQuerySchema = z.object({
  /** Local calendar date (YYYY-MM-DD) of the Monday of the reviewed week. */
  weekStart: dateStringSchema.optional(),
});
export type ReviewWeekQuery = z.infer<typeof reviewWeekQuerySchema>;

export const settingsUpdateSchema = z
  .object({
    timezone: z.string().min(1).max(64).optional(),
    dailyBriefTime: timeStringSchema.optional(),
    clockoutTime: timeStringSchema.optional(),
    endOfDayTime: timeStringSchema.optional(),
    weeklyReviewDay: z.coerce.number().int().min(0).max(6).optional(),
    weeklyReviewTime: timeStringSchema.optional(),
    dailyBriefEnabled: z.boolean().optional(),
    clockoutEnabled: z.boolean().optional(),
    endOfDayEnabled: z.boolean().optional(),
    weeklyReviewEnabled: z.boolean().optional(),
    greetingWeekdayLocation: z.string().trim().min(1).max(100).optional(),
    greetingWeekendLocation: z.string().trim().min(1).max(100).optional(),
    greetingScheduleEnabled: z.boolean().optional(),
    greetingTime: timeStringSchema.optional(),
    greetingMondayTime: timeStringSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type SettingsUpdateInput = z.infer<typeof settingsUpdateSchema>;

export const loginSchema = z.object({
  password: z.string().min(1).max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Change-password body validation (frontend UX + backend entry point).
 *
 * Only presence and length are enforced here so the backend can distinguish
 * failure reasons with specific error codes (field mismatch → VALIDATION_ERROR,
 * wrong current password → INVALID_CURRENT_PASSWORD, reuse →
 * PASSWORD_REUSE_NOT_ALLOWED). No composition rules: passphrases and spaces
 * are welcome.
 */
export const changePasswordSchema = z.object({
  currentPassword: z
    .string()
    .min(1, 'Current password is required.')
    .max(PASSWORD_POLICY.maxLength, 'Current password must be at most 128 characters.'),
  newPassword: z
    .string()
    .min(PASSWORD_POLICY.minLength, 'New password must be at least 10 characters.')
    .max(PASSWORD_POLICY.maxLength, 'New password must be at most 128 characters.'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your new password.')
    .max(PASSWORD_POLICY.maxLength, 'Password confirmation must be at most 128 characters.'),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** Discord interaction payloads (validated at the boundary). */
export const discordUserSchema = z.object({
  id: z.string().regex(/^\d{17,20}$/),
  username: z.string().optional(),
  global_name: z.string().nullable().optional(),
});

export const discordInteractionSchema = z.object({
  id: z.string(),
  application_id: z.string(),
  type: z.number().int(),
  token: z.string(),
  version: z.number().int().optional(),
  guild_id: z.string().optional(),
  channel_id: z.string().optional(),
  member: z
    .object({
      user: discordUserSchema.optional(),
    })
    .optional(),
  user: discordUserSchema.optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});
export type DiscordInteraction = z.infer<typeof discordInteractionSchema>;
